import firebase from 'firebase/app';
import 'firebase/functions';

/**
 * Calls to the Phase 1 server functions (platform/functions). Each one checks the
 * caller's role on the server; the browser holds no admin power of its own.
 *
 * REACT_APP_ADMIN_FUNCTIONS=true turns these screens on. The live site is built without
 * it, so it keeps the old screens until the functions are deployed there.
 */
export const adminFunctionsEnabled = process.env.REACT_APP_ADMIN_FUNCTIONS === 'true';
const region = process.env.REACT_APP_FUNCTIONS_REGION || 'australia-southeast1';

export type Role = 'client' | 'viewer' | 'member' | 'admin' | 'owner';
export const ROLES: { value: Role; label: string; help: string }[] = [
  {
    value: 'client',
    label: 'Client',
    help: "Sees only the projects for the client or agency names you choose, in a simple download page. Can't see or change anything else",
  },
  { value: 'viewer', label: 'Viewer', help: 'Can look and download, not change anything' },
  { value: 'member', label: 'Member', help: 'Can add and edit projects, records and files' },
  { value: 'admin', label: 'Admin', help: 'Member, plus delete, settings and managing users' },
  { value: 'owner', label: 'Owner', help: 'Admin, plus managing other admins and owners' },
];
/** client and no role are 0; viewer 1 … owner 4 (same as the server) */
export const roleRank = (r: unknown) => ['viewer', 'member', 'admin', 'owner'].indexOf(r as string) + 1;

export type ManagedUser = {
  uid: string;
  email: string | null;
  disabled: boolean;
  role: Role | null;
  /** client logins only: the client/agency names it can see, and whether masters can be downloaded */
  clients: string[] | null;
  clientMasters: boolean | null;
  legacyAdmin: boolean;
  createdAt: string | null;
  lastSignInAt: string | null;
  providers: string[];
};

export type ClientAccess = { clients: string[]; clientMasters: boolean };

/** "One3six, One3six Creative" -> ['One3six', 'One3six Creative'] */
export const parseClientNames = (text: string) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter((s, i, all) => s && all.findIndex((o) => o.toLowerCase() === s.toLowerCase()) === i);

// ---- client portal (task #1) ---------------------------------------------------

export type PortalProject = {
  id: string;
  title: string;
  client: string;
  agency: string;
  jobNumber: string;
  date: number | null;
  complete: boolean;
  featureURL: string | null;
};
export type PortalFile = {
  kind: 'videos' | 'assets';
  id: string;
  name: string;
  recordID?: string | null;
  category?: string | null;
  size: number | null;
  date: number | null;
  notes?: string;
  locked?: boolean;
};
export type PortalProjectDetail = {
  project: PortalProject;
  canDownloadMasters: boolean;
  records: { id: string; title: string; keyNumber: string }[];
  files: PortalFile[];
  assets: PortalFile[];
};
/** Admins previewing what a client login sees pass the names here; a client login passes nothing. */
export type PortalPreview = { previewClients: string[]; previewMasters: boolean } | undefined;

export const portalApi = {
  list: (preview?: PortalPreview) =>
    call<{ clients: string[]; preview: boolean; canDownloadMasters: boolean; projects: PortalProject[] }>('clientPortal', {
      action: 'list',
      ...preview,
    }),
  project: (projectId: string, preview?: PortalPreview) =>
    call<PortalProjectDetail>('clientPortal', { action: 'project', projectId, ...preview }),
  link: (kind: PortalFile['kind'], id: string, play: boolean, preview?: PortalPreview) =>
    call<{ url: string; expiresInMinutes: number }>('clientPortal', { action: play ? 'play' : 'download', kind, id, ...preview }),
};

// ---- Deleted items bin (5 Nov 2026) ---------------------------------------------
export type BinKind = 'videos' | 'assets' | 'gallery';
export type LeftoverCounts = Record<BinKind, { files: number; bytes: number }>;
export type BinRestoreResult = { restored?: boolean; needsProject?: boolean; how?: string; message: string };

async function call<T>(name: string, data?: unknown): Promise<T> {
  try {
    const result = await firebase.app().functions(region).httpsCallable(name)(data);
    return result.data as T;
  } catch (e) {
    // Callable errors carry the server's message; surface it rather than "INTERNAL".
    throw new Error((e as { message?: string }).message || `${name} failed`);
  }
}

export const adminApi = {
  listUsers: () => call<{ users: ManagedUser[]; orphanProfiles: { uid: string; email: string | null }[] }>('adminListUsers'),
  setDisabled: (uid: string, disabled: boolean) => call('adminSetDisabled', { uid, disabled }),
  setRole: (uid: string, role: Role | null, client?: ClientAccess) => call('adminSetRole', { uid, role, ...client }),
  createUser: (email: string, role: Role, client?: ClientAccess) =>
    call<{ uid: string; email: string; role: Role; setupLink: string }>('adminCreateUser', { email, role, ...client }),
  deleteUser: (uid: string, confirmEmail: string) => call('adminDeleteUser', { uid, confirmEmail }),
  resetLink: (uid: string) => call<{ email: string; link: string }>('adminPasswordResetLink', { uid }),
  restoreDeleted: (activityId: string) => call<{ restored: string; note: string | null }>('adminRestoreDeleted', { activityId }),
  rebuildSearch: () => call<{ chunkCount: number; gzipBytes: number; counts: Record<string, number> }>('adminRebuildSearchIndex'),
  /** dryRun true: count the files with no project; false: move them to Deleted items. */
  binLeftovers: (dryRun: boolean) => call<{ dryRun: boolean; counts: LeftoverCounts; binned: number }>('adminBinLeftovers', { dryRun }),
  binRestore: (kind: BinKind, id: string, projectID?: string) => call<BinRestoreResult>('adminBinRestore', { kind, id, projectID }),
};
